import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  FiChevronDown,
  FiTarget,
  FiGlobe,
  FiZap,
  FiAlertTriangle,
  FiCheckCircle,
  FiArrowUp,
  FiArrowDown,
  FiMinus,
  FiUsers,
  FiHome,
  FiCalendar,
} from "react-icons/fi";
import API_BASE_URL from "../../config/api";
import ConsolidatedOrbitChart from "../../components/ConsolidatedOrbitChart";
import { useAuth } from "../../context/AuthContext";
import { getZoneColor, getScoreZone } from "../../utils/zoneUtils";

// ─── Category mapping ────────────────────────────────────────────────────────
// Backend category names → display names used in the 3 school-influence areas
const INFLUENCE_CATEGORIES: { display: string; aliases: string[] }[] = [
  {
    display: "Friends",
    aliases: ["Friends / Peers", "Friends/Peers", "Friends", "Peers", "Social"],
  },
  {
    display: "Family",
    aliases: ["Family", "Parents", "Home"],
  },
  {
    display: "School Environment",
    aliases: [
      "School Environment",
      "School",
      "Teachers",
      "Society / Environment / Surroundings",
      "Society",
      "Environment",
    ],
  },
];

/** Find the display label for a backend category name */
const matchCategory = (backendName: string): string | null => {
  const lower = backendName.toLowerCase();
  for (const cat of INFLUENCE_CATEGORIES) {
    if (cat.aliases.some((a) => lower.includes(a.toLowerCase()))) {
      return cat.display;
    }
  }
  return null;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const getScoreColor = (score: number) => {
  return getZoneColor(getScoreZone(score)).color;
};

// ─── CustomSelect ─────────────────────────────────────────────────────────────
const CustomSelect = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string; isHeader?: boolean }[];
  onChange: (val: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.value === value) || options[0];

  return (
    <div className="relative group" ref={dropdownRef}>
      <label className="absolute -top-2.5 left-3 px-1 bg-white text-[10px] font-bold uppercase tracking-wider z-10 rounded text-[#1ecab8]">
        {label}
      </label>
      <div
        className="relative flex items-center justify-between min-w-[160px] pl-4 pr-3 py-3 bg-white border border-gray-300 hover:border-[#1ecab8]/30 rounded-xl text-sm font-medium text-gray-700 outline-none transition-all cursor-pointer shadow-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate pr-2">{selectedOption?.label}</span>
        <FiChevronDown
          size={18}
          className={`text-gray-400 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full max-h-64 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-[0_10px_40px_rgb(0,0,0,0.12)] py-1.5 z-50 origin-top">
          {options.map((opt) =>
            opt.isHeader ? (
              <div
                key={opt.value}
                className="px-4 py-2 text-[10px] font-black text-slate-400 uppercase tracking-wider bg-slate-50/80 mt-1 first:mt-0"
              >
                {opt.label}
              </div>
            ) : (
              <div
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                className={`px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors ${
                  value === opt.value
                    ? "bg-[#1ecab8]/10 text-[#1ecab8]"
                    : "text-gray-600 hover:bg-[#1ecab8]/5"
                }`}
              >
                {opt.label}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

// ── AI Insights Panel ──────────────────────────────────────────────────────────
const AIInsightsPanel = ({
  radarData,
  primaryName,
  secondaryName,
}: {
  radarData: { subject: string; primary: number; secondary: number }[];
  primaryName: string;
  secondaryName: string;
}) => {
  const insights = useMemo(() => {
    type InsightType = "strength" | "gap" | "parity";
    const result: {
      type: InsightType;
      icon: any;
      color: string;
      bg: string;
      border: string;
      badge: string;
      badgeBg: string;
      title: string;
      text: string;
      diff: number;
    }[] = [];

    radarData.forEach((d) => {
      const diff = d.primary - d.secondary;
      if (diff >= 6) {
        result.push({
          type: "strength",
          icon: FiCheckCircle,
          color: "text-emerald-600",
          bg: "bg-emerald-50",
          border: "border-emerald-200",
          badge: "STRENGTH",
          badgeBg: "bg-emerald-100 text-emerald-700",
          title: `${d.subject} — Above Benchmark`,
          text: `Your school scores ${d.primary} in ${d.subject}, which is ${Math.abs(diff)} pts above the benchmark (${d.secondary}). This is a key wellbeing advantage — maintain and celebrate it.`,
          diff,
        });
      } else if (diff <= -6) {
        result.push({
          type: "gap",
          icon: FiAlertTriangle,
          color: "text-rose-500",
          bg: "bg-rose-50",
          border: "border-rose-200",
          badge: "ACTION NEEDED",
          badgeBg: "bg-rose-100 text-rose-700",
          title: `${d.subject} — Below Benchmark`,
          text: `Your school scores ${d.primary} in ${d.subject}, which is ${Math.abs(diff)} pts below the benchmark (${d.secondary}). Consider targeted support programmes in this area.`,
          diff,
        });
      } else {
        result.push({
          type: "parity",
          icon: FiMinus,
          color: "text-blue-500",
          bg: "bg-blue-50",
          border: "border-blue-200",
          badge: "ON TRACK",
          badgeBg: "bg-blue-100 text-blue-700",
          title: `${d.subject} — On Par with Benchmark`,
          text: `Your school scores ${d.primary} in ${d.subject}, closely matching the benchmark of ${d.secondary}. Continue monitoring to maintain or improve this standing.`,
          diff,
        });
      }
    });

    return result.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
  }, [radarData, secondaryName]);

  const avgSchool = Math.round(
    radarData.reduce((s, d) => s + d.primary, 0) / (radarData.length || 1)
  );
  const avgEnsightly = Math.round(
    radarData.reduce((s, d) => s + d.secondary, 0) / (radarData.length || 1)
  );
  const overallGap = avgSchool - avgEnsightly;
  const strengths = insights.filter((i) => i.type === "strength").length;
  const gaps = insights.filter((i) => i.type === "gap").length;
  const onTrack = insights.length - strengths - gaps;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
            <FiZap size={18} className="text-violet-600" />
          </div>
          <div>
            <h3 className="text-sm font-black text-slate-800">AI Insights</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              <span className="font-semibold text-teal-600">{primaryName}</span>
              {" "}vs{" "}
              <span className="font-semibold text-blue-600">{secondaryName}</span>
            </p>
          </div>
        </div>
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50 px-3 py-1 rounded-full border border-slate-200">
          Auto-generated
        </span>
      </div>

      {/* Summary Stats Strip */}
      <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
        <div className="px-6 py-4 text-center">
          <p className="text-2xl font-black text-slate-800">{avgSchool}</p>
          <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">Your Avg Score</p>
        </div>
        <div className="px-6 py-4 text-center">
          <div
            className={`inline-flex items-center gap-1.5 text-2xl font-black ${overallGap > 0 ? "text-emerald-600" : overallGap < 0 ? "text-rose-500" : "text-slate-500"}`}
          >
            {overallGap > 0 ? (
              <FiArrowUp size={18} />
            ) : overallGap < 0 ? (
              <FiArrowDown size={18} />
            ) : (
              <FiMinus size={18} />
            )}
            {Math.abs(overallGap)}
          </div>
          <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">
            {overallGap > 0 ? "Above" : overallGap < 0 ? "Below" : "On Par with"} Benchmark
          </p>
        </div>
        <div className="px-6 py-4 text-center">
          <p className="text-2xl font-black text-slate-800">{avgEnsightly}</p>
          <p className="text-[10px] font-bold text-slate-400 uppercase mt-1">Benchmark Avg</p>
        </div>
      </div>

      {/* Badge Strip */}
      <div className="px-6 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-3">
        <span className="text-[11px] font-bold text-slate-500">Summary:</span>
        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700">
          {strengths} Strength{strengths !== 1 ? "s" : ""}
        </span>
        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-100 text-rose-700">
          {gaps} Gap{gaps !== 1 ? "s" : ""}
        </span>
        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-100 text-blue-700">
          {onTrack} On Track
        </span>
      </div>

      {/* Insight Cards */}
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {insights.map((insight, idx) => {
          const Icon = insight.icon;
          const barWidth = Math.min(Math.abs(insight.diff) * 5, 100);
          return (
            <div
              key={idx}
              className={`rounded-xl border ${insight.border} ${insight.bg} p-4 flex flex-col gap-3`}
            >
              <div className="flex items-start justify-between">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shadow-sm shrink-0">
                  <Icon size={16} className={insight.color} />
                </div>
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${insight.badgeBg}`}>
                  {insight.badge}
                </span>
              </div>
              <div>
                <h4 className="text-sm font-black text-slate-800 mb-1">{insight.title}</h4>
                <p className="text-xs text-slate-600 leading-relaxed">{insight.text}</p>
              </div>
              <div className="mt-auto pt-3 border-t border-white/60 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-white/80 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      insight.type === "strength"
                        ? "bg-emerald-500"
                        : insight.type === "gap"
                        ? "bg-rose-500"
                        : "bg-blue-400"
                    }`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
                <span
                  className={`text-[11px] font-black shrink-0 ${
                    insight.diff > 0
                      ? "text-emerald-600"
                      : insight.diff < 0
                      ? "text-rose-600"
                      : "text-blue-600"
                  }`}
                >
                  {insight.diff > 0 ? "+" : ""}
                  {insight.diff} pts
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const SchoolOrbit: React.FC = () => {
  const { user } = useAuth();
  const schoolId = user?.schoolId || (user?.userType === "school" ? user?.id : undefined);

  const [loading, setLoading] = useState(true);
  const [radarData, setRadarData] = useState<
    { subject: string; primary: number; secondary: number; fullMark: number }[]
  >([]);

  // Global Period
  const [selectedPeriod, setSelectedPeriod] = useState("all_time");

  // School Filters
  const [schoolFilters, setSchoolFilters] = useState({
    country: "all",
    state: "all",
    area: "all",
    board: "all",
    grade: "all",
  });

  // Ensightly Average Filters
  const [averageFilters, setAverageFilters] = useState({
    country: "all",
    state: "all",
    area: "all",
    board: "all",
    grade: "all",
  });

  const updateSchoolFilter = (key: string, val: string) => {
    setSchoolFilters((prev) => ({ ...prev, [key]: val }));
  };

  const updateAverageFilter = (key: string, val: string) => {
    setAverageFilters((prev) => ({ ...prev, [key]: val }));
  };

  const countryOptions = [
    { value: "all", label: "All Countries" },
    { value: "India", label: "India" },
  ];
  const stateOptions = [
    { value: "all", label: "All States" },
    { value: "Maharashtra", label: "Maharashtra" },
    { value: "Delhi", label: "Delhi" },
  ];
  const areaOptions = [
    { value: "all", label: "All Areas" },
    { value: "Mumbai", label: "Mumbai" },
    { value: "Pune", label: "Pune" },
  ];
  const boardOptions = [
    { value: "all", label: "All Boards" },
    { value: "CBSE", label: "CBSE" },
    { value: "ICSE", label: "ICSE" },
    { value: "IB", label: "IB" },
    { value: "State Board", label: "State Board" },
  ];
  const gradeOptions = [
    { value: "all", label: "All Grades" },
    { value: "1", label: "Grade 1" },
    { value: "2", label: "Grade 2" },
    { value: "3", label: "Grade 3" },
    { value: "4", label: "Grade 4" },
    { value: "5", label: "Grade 5" },
    { value: "6", label: "Grade 6" },
    { value: "7", label: "Grade 7" },
    { value: "8", label: "Grade 8" },
    { value: "9", label: "Grade 9" },
    { value: "10", label: "Grade 10" },
    { value: "11", label: "Grade 11" },
    { value: "12", label: "Grade 12" },
  ];
  const periodOptions = [
    { value: "current_month", label: "Current Month" },
    { value: "last_month", label: "Last Month" },
    { value: "last_quarter", label: "Last Quarter" },
    { value: "year", label: "Year to Date" },
    { value: "all_time", label: "All Time" },
  ];

  // ── Fetch real data from the external benchmarking endpoint ─────────────────
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams({ type: "external", period: selectedPeriod });
        if (schoolId) q.append("schoolId", schoolId.toString());
        
        // School parameters
        if (schoolFilters.board !== "all") q.append("schoolBoard", schoolFilters.board);
        if (schoolFilters.grade !== "all") q.append("schoolGrade", schoolFilters.grade);
        if (schoolFilters.area !== "all") q.append("schoolCity", schoolFilters.area);
        if (schoolFilters.state !== "all") q.append("schoolState", schoolFilters.state);
        
        // Ensightly parameters
        if (averageFilters.board !== "all") q.append("avgBoard", averageFilters.board);
        if (averageFilters.grade !== "all") q.append("avgGrade", averageFilters.grade);
        if (averageFilters.area !== "all") q.append("avgCity", averageFilters.area);
        if (averageFilters.state !== "all") q.append("avgState", averageFilters.state);

        const res = await window.fetch(`${API_BASE_URL}/schools/analytics/benchmarking?${q}`);
        const json = await res.json();

        // json: { school: { categories: [{category, score}] }, ensightly: { categories: [{category, score}] } }
        const schoolCategories: { category: string; score: number }[] =
          json?.school?.categories || [];
        const ensightlyCategories: { category: string; score: number }[] =
          json?.ensightly?.categories || [];

        // Build radar data for the 3 school-influence categories
        const built = INFLUENCE_CATEGORIES.map((cat) => {
          // Find matching school score
          const schoolMatch = schoolCategories.find((c) => {
            const label = matchCategory(c.category);
            return label === cat.display;
          });
          // Find matching ensightly/benchmark score
          const ensightlyMatch = ensightlyCategories.find((c) => {
            const label = matchCategory(c.category);
            return label === cat.display;
          });

          return {
            subject: cat.display,
            primary: schoolMatch?.score ?? 0,
            secondary: ensightlyMatch?.score ?? 0,
            fullMark: 100,
          };
        });

        setRadarData(built);
      } catch (e) {
        console.error("SchoolOrbit fetch error:", e);
        // Fallback — empty data so chart still renders without crashing
        setRadarData(
          INFLUENCE_CATEGORIES.map((cat) => ({
            subject: cat.display,
            primary: 0,
            secondary: 0,
            fullMark: 100,
          }))
        );
      }
      setLoading(false);
    };

    fetchData();
  }, [schoolId, selectedPeriod, schoolFilters, averageFilters]);

  const primaryName = `Your School${schoolFilters.board !== "all" ? ` (${schoolFilters.board})` : ""}${schoolFilters.grade !== "all" ? `, Grade ${schoolFilters.grade}` : ""}`;
  const secondaryName = "Ensightly National Avg";

  return (
    <div className="space-y-6 animate-fadeIn pb-12">

      {/* ── Period Selector ─────────────────────────────────────────────────── */}
      <div className="flex justify-end">
        <div className="w-48 shadow-sm">
          <CustomSelect
            label="Time Period"
            value={selectedPeriod}
            options={periodOptions}
            onChange={setSelectedPeriod}
          />
        </div>
      </div>

      {/* ── Filter Panels ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Your School Panel */}
        <div className="bg-white/80 backdrop-blur-xl p-5 rounded-2xl shadow-sm border border-slate-200/80 relative overflow-visible">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-teal-50 text-teal-600 rounded-lg">
              <FiTarget size={18} />
            </div>
            <h3 className="font-bold text-slate-800 text-sm">Filter Your School Data</h3>
          </div>
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-5">
            <CustomSelect
              label="Country"
              value={schoolFilters.country}
              options={countryOptions}
              onChange={(v) => updateSchoolFilter("country", v)}
            />
            <CustomSelect
              label="State"
              value={schoolFilters.state}
              options={stateOptions}
              onChange={(v) => updateSchoolFilter("state", v)}
            />
            <CustomSelect
              label="Area/City"
              value={schoolFilters.area}
              options={areaOptions}
              onChange={(v) => updateSchoolFilter("area", v)}
            />
            <CustomSelect
              label="Board"
              value={schoolFilters.board}
              options={boardOptions}
              onChange={(v) => updateSchoolFilter("board", v)}
            />
            <CustomSelect
              label="Grade"
              value={schoolFilters.grade}
              options={gradeOptions}
              onChange={(v) => updateSchoolFilter("grade", v)}
            />
          </div>
        </div>

        {/* Ensightly Average Panel */}
        <div className="bg-white/80 backdrop-blur-xl p-5 rounded-2xl shadow-sm border border-slate-200/80 relative overflow-visible">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <FiGlobe size={18} />
            </div>
            <h3 className="font-bold text-slate-800 text-sm">Filter Ensightly National Avg</h3>
          </div>
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-5">
            <CustomSelect
              label="Country"
              value={averageFilters.country}
              options={countryOptions}
              onChange={(v) => updateAverageFilter("country", v)}
            />
            <CustomSelect
              label="State"
              value={averageFilters.state}
              options={stateOptions}
              onChange={(v) => updateAverageFilter("state", v)}
            />
            <CustomSelect
              label="Area/City"
              value={averageFilters.area}
              options={areaOptions}
              onChange={(v) => updateAverageFilter("area", v)}
            />
            <CustomSelect
              label="Board"
              value={averageFilters.board}
              options={boardOptions}
              onChange={(v) => updateAverageFilter("board", v)}
            />
            <CustomSelect
              label="Grade"
              value={averageFilters.grade}
              options={gradeOptions}
              onChange={(v) => updateAverageFilter("grade", v)}
            />
          </div>
        </div>
      </div>

      {/* ── Chart + Influence Benchmark + Insights ────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-200/80 border-t-[#1ecab8]" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Orbit Chart */}
          {(() => {
            const overallScore = radarData.length > 0 
              ? Math.round(radarData.reduce((acc, d) => acc + d.primary, 0) / radarData.length)
              : 0;
            return (
              <ConsolidatedOrbitChart
                data={radarData}
                primaryName={primaryName}
                secondaryName={secondaryName}
                overallScore={overallScore}
              />
            );
          })()}

          {/* School Influence Benchmark — bar comparison */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-black text-slate-800">School Influence Benchmark</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Comparing your school's three influence areas against the Ensightly national average.
                </p>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-slate-400 font-medium">
                <FiCalendar size={12} />
                {periodOptions.find((p) => p.value === selectedPeriod)?.label}
              </div>
            </div>

            {radarData.every((r) => r.primary === 0) ? (
              <div className="text-center py-10 text-slate-400 text-sm">
                No data available for the selected filters.
              </div>
            ) : (
              <div className="space-y-4 max-w-5xl">
                {radarData.map((row) => {
                  const Icon =
                    row.subject === "Friends"
                      ? FiUsers
                      : row.subject === "Family"
                      ? FiHome
                      : FiGlobe;
                  const diff = row.primary - row.secondary;
                  return (
                    <div
                      key={row.subject}
                      className="grid grid-cols-[200px_1fr_1fr_100px] gap-6 items-center p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition-colors shadow-sm bg-slate-50/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-indigo-500 shadow-sm shrink-0">
                          <Icon size={18} />
                        </div>
                        <span className="text-sm font-bold text-slate-800 leading-tight">
                          {row.subject}
                        </span>
                      </div>

                      {/* Your School bar */}
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-center">
                          <span className="text-[11px] font-bold text-slate-500 uppercase truncate pr-2">
                            {primaryName}
                          </span>
                          <span
                            className="text-sm font-black"
                            style={{ color: getScoreColor(row.primary) }}
                          >
                            {row.primary}
                          </span>
                        </div>
                        <div className="bg-slate-200 rounded-full h-2.5 overflow-hidden w-full relative">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${row.primary}%`,
                              backgroundColor: getScoreColor(row.primary),
                            }}
                          />
                        </div>
                      </div>

                      {/* National Average bar */}
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-center">
                          <span className="text-[11px] font-bold text-slate-500 uppercase truncate pr-2">
                            {secondaryName}
                          </span>
                          <span
                            className="text-sm font-black"
                            style={{ color: getScoreColor(row.secondary) }}
                          >
                            {row.secondary}
                          </span>
                        </div>
                        <div className="bg-slate-200 rounded-full h-2.5 overflow-hidden w-full relative">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${row.secondary}%`,
                              backgroundColor: getScoreColor(row.secondary),
                            }}
                          />
                        </div>
                      </div>

                      {/* Diff badge */}
                      <div className="text-right flex items-center justify-end">
                        <span
                          className={`inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs font-black ${
                            diff > 0
                              ? "bg-emerald-100 text-emerald-700"
                              : diff < 0
                              ? "bg-red-100 text-red-700"
                              : "bg-slate-200 text-slate-600"
                          }`}
                        >
                          {diff > 0 ? "+" : ""}
                          {diff}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <AIInsightsPanel
            radarData={radarData}
            primaryName={primaryName}
            secondaryName={secondaryName}
          />
        </div>
      )}
    </div>
  );
};

export default SchoolOrbit;
